import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX, Star } from "lucide-react";
import { AnimatedSection } from "@/components/animations/AnimatedSection";

interface UGCVideo {
  id: number;
  videoUrl: string;
  username: string;
  rating: number;
}

const ugcVideos: UGCVideo[] = [
  {
    id: 1,
    videoUrl: "/videos/ugc/ugc-1.mp4",
    username: "Verified Customer",
    rating: 5,
  },
  {
    id: 2,
    videoUrl: "/videos/ugc/ugc-2.mp4",
    username: "Verified Customer",
    rating: 5,
  },
  {
    id: 3,
    videoUrl: "/videos/ugc/ugc-3.mp4",
    username: "Verified Customer",
    rating: 4.5,
  },
  {
    id: 4,
    videoUrl: "/videos/ugc/ugc-4.mp4",
    username: "Verified Customer",
    rating: 5,
  },
  {
    id: 5,
    videoUrl: "/videos/ugc/ugc-5.mp4",
    username: "Verified Customer",
    rating: 4.5,
  },
  {
    id: 6,
    videoUrl: "/videos/ugc/ugc-6.mp4",
    username: "Verified Customer",
    rating: 5,
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

interface VideoCardProps {
  video: UGCVideo;
  index: number;
}

const VideoCard = ({ video, index }: VideoCardProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isInView, setIsInView] = useState(false);

  // Lazy load videos only when they enter viewport
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Advance video by 0.4s when metadata is loaded
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0.4;
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <motion.div
      ref={containerRef}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      variants={fadeInUp}
      transition={{ delay: index * 0.1 }}
      className="group relative aspect-[9/16] rounded-xl overflow-hidden bg-muted cursor-pointer"
      onClick={togglePlay}
    >
      {isInView && (
        <video
          ref={videoRef}
          src={video.videoUrl}
          muted={isMuted}
          loop
          playsInline
          preload="metadata"
          onLoadedMetadata={handleLoadedMetadata}
          className="w-full h-full object-cover"
        />
      )}

      {/* Rating badge */}
      <div className="absolute top-2 left-2 flex items-center gap-0.5 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1">
        {[...Array(Math.floor(video.rating))].map((_, i) => (
          <Star key={i} className="h-2.5 w-2.5 text-yellow-400 fill-yellow-400" />
        ))}
        {video.rating % 1 !== 0 && (
          <Star className="h-2.5 w-2.5 text-yellow-400 fill-yellow-400/50" />
        )}
      </div>

      {/* Play/Pause overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className={`w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center transition-opacity ${isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}>
          {isPlaying ? (
            <Pause className="h-5 w-5 text-white fill-white" />
          ) : (
            <Play className="h-5 w-5 text-white fill-white ml-1" />
          )}
        </div>
      </div>

      {/* Mute button */}
      <button
        onClick={toggleMute}
        aria-label={isMuted ? "Unmute video" : "Mute video"}
        className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {isMuted ? (
          <VolumeX className="h-4 w-4 text-white" />
        ) : (
          <Volume2 className="h-4 w-4 text-white" />
        )}
      </button>

      {/* Username */}
      <div className="absolute bottom-2 left-2 right-10">
        <span className="text-white/80 text-xs font-medium bg-black/40 backdrop-blur-sm px-2 py-1 rounded-full">
          {video.username}
        </span>
      </div>
    </motion.div>
  );
};

interface UGCVideoSectionProps {
  variant?: "full" | "compact";
}

export const UGCVideoSection = ({ variant = "full" }: UGCVideoSectionProps) => {
  const displayVideos = variant === "compact" ? ugcVideos.slice(0, 4) : ugcVideos;

  return (
    <AnimatedSection className={variant === "full" ? "section-padding bg-secondary/30" : "py-8"}>
      <div className="container-calim">
        {variant === "full" && (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-8"
          >
            <p className="text-subhead mb-2">Real Customers, Real Stories</p>
            <h2 className="text-headline mb-3">See It In Action</h2>
            <p className="text-body-lg text-muted-foreground max-w-xl mx-auto">
              Watch how our community uses CALMISTYLE in their daily wellness rituals.
            </p>
          </motion.div>
        )}

        {variant === "compact" && (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="mb-6"
          >
            <h3 className="font-display text-xl font-light">Customer Videos</h3>
          </motion.div>
        )}

        {/* Video Grid */}
        <div className={`grid gap-3 ${
          variant === "full" 
            ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" 
            : "grid-cols-2 sm:grid-cols-4"
        }`}>
          {displayVideos.map((video, index) => (
            <VideoCard key={video.id} video={video} index={index} />
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};
